import { useEffect, useMemo, useState } from "react";
import { analyticsApi } from "@/lib/api/arrex/analytics";
import { useApp } from "@/contexts/AppContext";
import { formatPrice, localized, imgUrl } from "@/lib/format";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatCardsSkeleton, ChartSkeleton } from "@/components/ui/skeletons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,

  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";

type Range = "today" | "7d" | "30d" | "3m" | "1y" | "custom";

const RANGE_LABELS: Record<Range, string> = {
  today: "Today",
  "7d": "Last 7 Days",
  "30d": "Last 30 Days",
  "3m": "Last 3 Months",
  "1y": "Last Year",
  custom: "Custom",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "#b8801f",
  confirmed: "#250f03",
  shipped: "#3f6b4a",
  delivered: "#3f6b4a",
  returned: "#c5a059",
  cancelled: "#ba1a1a",
};

function rangeToDates(r: Range, customFrom?: string, customTo?: string): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date();
  if (r === "today") from.setHours(0, 0, 0, 0);
  else if (r === "7d") from.setDate(to.getDate() - 7);
  else if (r === "30d") from.setDate(to.getDate() - 30);
  else if (r === "3m") from.setMonth(to.getMonth() - 3);
  else if (r === "1y") from.setFullYear(to.getFullYear() - 1);
  else if (r === "custom" && customFrom && customTo) {
    return { from: new Date(customFrom), to: new Date(customTo) };
  }
  return { from, to };
}

export default function AdminAnalytics() {
  const { t } = useTranslation();
  const { lang } = useApp();
  const [range, setRange] = useState<Range>("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [orders, setOrders] = useState<any[] | null>(null);
  const [items, setItems] = useState<any[] | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [visits, setVisits] = useState<any[] | null>(null);
  const [clearing, setClearing] = useState(false);
  const [clearingSales, setClearingSales] = useState(false);
  const [salesResetAt, setSalesResetAt] = useState<Date | null>(() => {
    const v = localStorage.getItem("analytics_sales_reset_at");
    return v ? new Date(v) : null;
  });

  const loadData = () => {
    analyticsApi
      .get()
      .then(({ orders, items, products, visits }: any) => {
        setOrders(orders || []);
        setItems(items || []);
        setProducts(products || []);
        setVisits(visits || []);
      })
      .catch(() => {
        setOrders([]);
        setItems([]);
        setProducts([]);
        setVisits([]);
      });
  };

  useEffect(() => {
    loadData();
  }, [lang]);

  const { from, to } = rangeToDates(range, customFrom, customTo);

  // Invalid orders (returned/cancelled) — excluded from sales analytics
  const INVALID_STATUSES = new Set(["returned", "cancelled"]);
  const invalidOrderIds = useMemo(
    () => new Set((orders || []).filter((o) => INVALID_STATUSES.has(o.status)).map((o) => o.id)),
    [orders],
  );

  const filteredOrders = useMemo(
    () =>
      (orders || []).filter((o) => {
        const d = new Date(o.created_at);
        if (salesResetAt && d < salesResetAt) return false;
        return d >= from && d <= to;
      }),
    [orders, from, to, salesResetAt],
  );

  // Valid orders only (exclude returned/cancelled) for sales metrics
  const validOrders = useMemo(() => filteredOrders.filter((o) => !INVALID_STATUSES.has(o.status)), [filteredOrders]);

  const filteredItems = useMemo(
    () =>
      (items || []).filter((i) => {
        const d = new Date(i.created_at);
        if (salesResetAt && d < salesResetAt) return false;
        return d >= from && d <= to && !invalidOrderIds.has(i.order_id);
      }),
    [items, from, to, invalidOrderIds, salesResetAt],
  );

  const totalRevenue = validOrders.reduce((s, o) => s + Number(o.total), 0);
  const totalOrders = validOrders.length;
  const avgOrder = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const conversionStatuses =
    filteredOrders.length > 0
      ? Math.round((filteredOrders.filter((o) => o.status === "delivered").length / filteredOrders.length) * 100)
      : 0;

  // Time-series — bucket by day or month depending on range span (valid orders only)
  const timeBuckets = useMemo(() => {
    const useMonth = range === "3m" || range === "1y";
    const map = new Map<string, { revenue: number; orders: number }>();
    validOrders.forEach((o) => {
      const d = new Date(o.created_at);
      const key = useMonth
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
        : `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
      const v = map.get(key) || { revenue: 0, orders: 0 };
      v.orders += 1;
      v.revenue += Number(o.total);
      map.set(key, v);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => ({ date: k, ...v }));
  }, [validOrders, range]);

  // Status distribution (all statuses for visibility)
  const statusData = useMemo(() => {
    const map = new Map<string, number>();
    filteredOrders.forEach((o) => map.set(o.status, (map.get(o.status) || 0) + 1));
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [filteredOrders]);

  // Best sellers — only items from valid (non-returned/cancelled) orders
  const bestSellers = useMemo(() => {
    const map = new Map<string, { qty: number; revenue: number }>();
    filteredItems.forEach((i) => {
      const v = map.get(i.product_id) || { qty: 0, revenue: 0 };
      v.qty += i.quantity;
      v.revenue += i.quantity * Number(i.unit_price);
      map.set(i.product_id, v);
    });
    const productById = (id: string) => products.find((p) => p.id === id);
    return Array.from(map.entries())
      .map(([id, v]) => ({
        id,
        name: productById(id) ? localized(productById(id)!, "name", lang) : "—",
        image: productById(id)?.main_image_url,
        ...v,
      }))
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 8);
  }, [filteredItems, products, lang]);

  // Visitor analytics
  const filteredVisits = useMemo(
    () =>
      (visits || []).filter((v) => {
        const d = new Date(v.created_at);
        return d >= from && d <= to;
      }),
    [visits, from, to],
  );

  const totalVisits = filteredVisits.length;
  const uniqueVisitors = useMemo(() => new Set(filteredVisits.map((v) => v.visitor_id)).size, [filteredVisits]);

  // Previous-period growth
  const periodMs = to.getTime() - from.getTime();
  const prevFrom = new Date(from.getTime() - periodMs);
  const prevVisits = (visits || []).filter((v) => {
    const d = new Date(v.created_at);
    return d >= prevFrom && d < from;
  }).length;
  const visitorGrowth =
    prevVisits === 0 ? (totalVisits > 0 ? 100 : 0) : Math.round(((totalVisits - prevVisits) / prevVisits) * 100);

  const visitsBuckets = useMemo(() => {
    const useMonth = range === "3m" || range === "1y";
    const map = new Map<string, { visits: number; uniques: Set<string> }>();
    filteredVisits.forEach((v) => {
      const d = new Date(v.created_at);
      const key = useMonth
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
        : `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
      const cur = map.get(key) || { visits: 0, uniques: new Set<string>() };
      cur.visits += 1;
      cur.uniques.add(v.visitor_id);
      map.set(key, cur);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => ({ date: k, visits: v.visits, unique: v.uniques.size }));
  }, [filteredVisits, range]);

  // Daily / weekly / monthly summary
  const now = new Date();
  const dayAgo = new Date(now.getTime() - 24 * 3600 * 1000);
  const weekAgo = new Date(now.getTime() - 7 * 24 * 3600 * 1000);
  const monthAgo = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
  const halfHourAgo = new Date(now.getTime() - 30 * 60 * 1000);
  const visitsSince = (since: Date) => (visits || []).filter((v) => new Date(v.created_at) >= since).length;
  const dailyVisits = visitsSince(dayAgo);
  const weeklyVisits = visitsSince(weekAgo);
  const monthlyVisits = visitsSince(monthAgo);

  // Active sessions = distinct sessions with activity in the last 30 minutes.
  const activeSessions = useMemo(() => {
    const set = new Set<string>();
    (visits || []).forEach((v) => {
      if (new Date(v.created_at) >= halfHourAgo) {
        set.add(v.session_id || v.visitor_id);
      }
    });
    return set.size;
  }, [visits]);

  if (!orders || !items || !visits) {
    return (
      <div className="space-y-6">
        <h1 className="font-display text-3xl font-bold">{t("admin.analytics.title")}</h1>
        <StatCardsSkeleton count={4} />
        <ChartSkeleton />
        <ChartSkeleton height={220} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-3xl font-bold">{t("admin.analytics.title")}</h1>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                disabled={clearingSales}
                className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">{t('admin.analyticsPage.clearSales')}</span>
                <span className="sm:hidden">{t('admin.common.clear')}</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear Sales Analytics?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action will permanently reset all sales analytics and reporting data (revenue, orders, best
                  sellers, charts). Your actual orders, products, customers and inventory will not be deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('admin.common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    if (clearingSales) return;
                    setClearingSales(true);
                    const now = new Date();
                    localStorage.setItem("analytics_sales_reset_at", now.toISOString());
                    setSalesResetAt(now);
                    setTimeout(() => setClearingSales(false), 400);
                    toast.success("Sales analytics cleared");
                  }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Confirm Clear
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {salesResetAt && (
            <Button
              size="sm"
              variant="ghost"
              className="text-xs text-muted-foreground"
              onClick={() => {
                localStorage.removeItem("analytics_sales_reset_at");
                setSalesResetAt(null);
                toast.success("Analytics history restored");
              }}
            >
              Restore history
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(RANGE_LABELS) as Range[]).map((r) => (
            <Button key={r} size="sm" variant={range === r ? "default" : "outline"} onClick={() => setRange(r)}>
              {RANGE_LABELS[r]}
            </Button>
          ))}
        </div>
      </div>

      {range === "custom" && (
        <div className="surface-card rounded-lg p-4 flex flex-wrap items-end gap-3">
          <div>
            <Label>{t('admin.common.from')}</Label>
            <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          </div>
          <div>
            <Label>{t('admin.common.to')}</Label>
            <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: "Total Sales", value: formatPrice(totalRevenue, "$", lang), accent: "text-primary" },
          { label: "Orders", value: totalOrders, accent: "" },
          { label: "Avg Order Value", value: formatPrice(avgOrder, "$", lang), accent: "text-secondary" },
          { label: "Delivered Rate", value: `${conversionStatuses}%`, accent: "text-success" },
        ].map((c) => (
          <div key={c.label} className="surface-card rounded-lg p-3 sm:p-4 lg:p-5 min-w-0">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider whitespace-normal break-words">
              {c.label}
            </p>
            <p
              className={`font-display text-sm sm:text-base md:text-lg lg:text-xl font-bold mt-1 sm:mt-2 ${c.accent} whitespace-normal break-words leading-tight`}
            >
              {c.value}
            </p>
          </div>
        ))}
      </div>

      {/* Revenue area chart */}
      <div className="surface-card rounded-lg p-6">
        <h2 className="font-display text-lg mb-4">{t('admin.analyticsPage.revenueOverTime')}</h2>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={timeBuckets}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#250f03" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#250f03" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#d4c3bc" />
              <XAxis dataKey="date" stroke="#50443f" fontSize={11} />
              <YAxis stroke="#50443f" fontSize={11} />
              <Tooltip
                contentStyle = {{
                  background: "#ffffff",
                  border: "1px solid #d4c3bc",
                  borderRadius: 8,
                }}
                formatter={(v: any) => formatPrice(Number(v), "$", lang)}
              />
              <Area type="monotone" dataKey="revenue" stroke="#250f03" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Orders bar + status pie */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="surface-card rounded-lg p-6">
          <h2 className="font-display text-lg mb-4">{t('admin.analyticsPage.ordersOverTime')}</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timeBuckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d4c3bc" />
                <XAxis dataKey="date" stroke="#50443f" fontSize={11} />
                <YAxis stroke="#50443f" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: "#ffffff",
                    border: "1px solid #d4c3bc",
                    borderRadius: 8,
                  }}
                />
                <Bar dataKey="orders" fill="#c5a059" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="surface-card rounded-lg p-6">
          <h2 className="font-display text-lg mb-4">{t('admin.analyticsPage.statusDistribution')}</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={3}
                >
                  {statusData.map((s, i) => (
                    <Cell key={i} fill={STATUS_COLORS[s.name] || "#f3f3f4"} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip
                  contentStyle={{
                    background: "#ffffff",
                    border: "1px solid #d4c3bc",
                    borderRadius: 8,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Visitors analytics */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-xl sm:text-2xl font-bold">{t('admin.analyticsPage.visitors')}</h2>
          <div className="flex items-center gap-2">
            <span
              className={`text-xs sm:text-sm font-medium px-2 py-1 rounded-md ${visitorGrowth >= 0 ? "text-success bg-success/10" : "text-destructive bg-destructive/10"}`}
            >
              {visitorGrowth >= 0 ? "▲" : "▼"} {Math.abs(visitorGrowth)}% vs prev period
            </span>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={clearing}
                  className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('admin.analyticsPage.clearVisitors')}</span>
                  <span className="sm:hidden">{t('admin.common.clear')}</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear Visitors Analytics?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action will permanently remove all stored visitors analytics data (total visits, unique
                    visitors, history and chart data). Orders, revenue and product analytics will not be affected.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('admin.common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={async () => {
                      if (clearing) return;
                      setClearing(true);
                      try {
                        await analyticsApi.clearVisits();
                        setVisits([]);
                        toast.success("Visitors analytics cleared");
                      } catch {
                        toast.error("Failed to clear visitors data");
                      } finally {
                        setClearing(false);
                      }
                    }}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Confirm Clear
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          {[
            { label: "Total Visits", value: totalVisits, accent: "text-primary" },
            { label: "Unique Visitors", value: uniqueVisitors, accent: "text-secondary" },
            { label: "Active Sessions", value: activeSessions, accent: "text-warning" },
            { label: "Last 24h", value: dailyVisits, accent: "" },
            { label: "Last 7 Days", value: weeklyVisits, accent: "text-success" },
          ].map((c) => (
            <div key={c.label} className="surface-card rounded-lg p-3 sm:p-4 lg:p-5 min-w-0">
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider whitespace-normal break-words">
                {c.label}
              </p>
              <p
                className={`font-display text-sm sm:text-base md:text-lg lg:text-xl font-bold mt-1 sm:mt-2 ${c.accent} whitespace-normal break-words leading-tight`}
              >
                {c.value.toLocaleString()}
              </p>
            </div>
          ))}
        </div>

        <div className="surface-card rounded-lg p-4 sm:p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
            <h3 className="font-display text-base sm:text-lg">{t('admin.analyticsPage.visitorsOverTime')}</h3>
            <span className="text-xs text-muted-foreground">Last 30 days: {monthlyVisits.toLocaleString()} visits</span>
          </div>
          <div className="h-64 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={visitsBuckets}>
                <defs>
                  <linearGradient id="vis" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#c5a059" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#c5a059" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#d4c3bc" />
                <XAxis dataKey="date" stroke="#50443f" fontSize={11} />
                <YAxis stroke="#50443f" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "#ffffff",
                    border: "1px solid #d4c3bc",
                    borderRadius: 8,
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="visits"
                  stroke="#c5a059"
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  animationDuration={800}
                />
                <Line
                  type="monotone"
                  dataKey="unique"
                  stroke="#250f03"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  animationDuration={800}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Best sellers */}
      <div className="surface-card rounded-lg p-6">
        <h2 className="font-display text-lg mb-4">{t('admin.analyticsPage.bestSellers')}</h2>
        {bestSellers.length === 0 ? (
          <p className="text-sm text-muted-foreground">No sales in this period.</p>
        ) : (
          <div className="space-y-2">
            {bestSellers.map((b, i) => (
              <div key={b.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 transition-colors">
                <span className="font-display text-lg font-bold text-muted-foreground w-6 text-center">#{i + 1}</span>
                {b.image && <img src={imgUrl(b.image)} className="w-10 h-10 rounded object-cover" />}
                <span className="flex-1 font-medium break-words">{b.name}</span>
                <span className="text-sm text-muted-foreground">{b.qty} sold</span>
                <span className="font-bold text-primary">{formatPrice(b.revenue, "$", lang)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
